import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import {
  createInventoryRevision,
  fetchStores,
  selectCreateRevisionError,
  selectCreateRevisionStatus,
  selectStores,
  selectStoresError,
  selectStoresStatus,
} from "../features/inventoryRevisions/inventoryRevisionsSlice";
import "./WarehouseRevisionCreatePage.css";

export default function WarehouseRevisionCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Single visible field: user types and selects from dropdown
  const [storeQuery, setStoreQuery] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [storesOpen, setStoresOpen] = useState(false);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const storesError = useSelector(selectStoresError);

  const [plannedFinishAt, setPlannedFinishAt] = useState(null);
  const [plannedFinishError, setPlannedFinishError] = useState(null);
  const [comment, setComment] = useState("");

  const createStatus = useSelector(selectCreateRevisionStatus);
  const createError = useSelector(selectCreateRevisionError);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  // Debounced store search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      dispatch(fetchStores({ search: storeQuery }));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dispatch, storeQuery]);

  function startOfToday() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  }

  // Default planned_finish_at to "now" to satisfy required field and reduce friction
  useEffect(() => {
    setPlannedFinishAt((prev) => (prev ? prev : new Date()));
  }, []);

  const canSubmit = useMemo(() => {
    return Boolean(selectedStoreId) && createStatus !== "saving";
  }, [createStatus, selectedStoreId]);

  const selectedStoreLabel = useMemo(() => {
    if (!selectedStoreId) return "";
    const s = stores.find((x) => String(x.id) === String(selectedStoreId));
    if (!s) return "";
    return `${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""} (#${s.id})`;
  }, [selectedStoreId, stores]);

  const visibleStores = useMemo(() => {
    // keep dropdown short
    return (stores || []).slice(0, 20);
  }, [stores]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setPlannedFinishError(null);
      try {
        if (!selectedStoreId) return;
        if (!plannedFinishAt || !(plannedFinishAt instanceof Date) || Number.isNaN(plannedFinishAt.getTime())) {
          setPlannedFinishError("Обязательное поле");
          return;
        }
        if (plannedFinishAt < startOfToday()) {
          setPlannedFinishError("Можно выбрать только сегодня и позже");
          return;
        }

        const created = await dispatch(
          createInventoryRevision({
            store: Number(selectedStoreId),
            planned_finish_at: plannedFinishAt.toISOString(),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/revision/${created?.id}`, { replace: true, state: { from: fromList } });
      } catch {
        // error is in redux
      }
    },
    [comment, dispatch, fromList, navigate, plannedFinishAt, selectedStoreId]
  );

  return (
    <div className="warehouse-revision-create-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/revision"} className="link">
            Ревизии
          </Link>
          <span className="sep">/</span>
          <span className="muted">Новая ревизия</span>
        </div>
        <div className="title">Создать ревизию</div>
        <div className="sub-title">
          Создание ревизии через <span className="mono">POST /api/product/inventory-revisions/</span>.
        </div>
      </div>

      {(createError || storesError) && <div className="error">{createError || storesError}</div>}

      <form onSubmit={onSubmit} className="form">
        <label className="field">
          <div className="label">Магазин</div>
          <div className="combo-wrap">
            <input
              value={storeQuery}
              onChange={(e) => {
                setStoreQuery(e.target.value);
                setSelectedStoreId(null);
                setStoresOpen(true);
              }}
              onFocus={() => setStoresOpen(true)}
              placeholder="Начни вводить название/адрес магазина…"
              className="input"
            />
            {storesOpen && (
              <div className="dropdown">
                <div className="dropdown-hint">
                  {storesStatus === "loading"
                    ? "Загрузка…"
                    : selectedStoreLabel
                      ? `Выбрано: ${selectedStoreLabel}`
                      : "Выбери магазин из списка ниже"}
                </div>
                {visibleStores.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setSelectedStoreId(s.id);
                      setStoreQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                      setStoresOpen(false);
                    }}
                  >
                    <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                    <div className="dropdown-sub">
                      {s.address || "—"} · #{s.id}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="help">
            В запросе будет отправлено поле <span className="mono">store</span> (id магазина).
          </div>
        </label>

        <label className="field">
          <div className="label">planned_finish_at</div>
          <DatePicker
            selected={plannedFinishAt}
            onChange={(d) => setPlannedFinishAt(d)}
            showTimeSelect
            timeIntervals={15}
            timeFormat="HH:mm"
            dateFormat="dd.MM.yyyy HH:mm"
            minDate={startOfToday()}
            customInput={<input className="input" />}
          />
          {plannedFinishError && <div className="inline-error">{plannedFinishError}</div>}
          <div className="help">
            Выбрано: {plannedFinishAt ? plannedFinishAt.toLocaleString("ru-RU") : "—"} (можно только сегодня и позже)
          </div>
        </label>

        <label className="field">
          <div className="label">comment</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Комментарий…"
            className="textarea"
            rows={4}
          />
        </label>

        <div className="actions">
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {createStatus === "saving" ? "Создаю…" : "Создать"}
          </button>
          <Link to={fromList || "/warehouse/revision"} className="btn-ghost">
            Отмена
          </Link>
        </div>
      </form>
    </div>
  );
}

